import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  Assignment as AssignmentIcon,
  Dashboard as DashboardIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  Person as PersonIcon,
  Topic as TopicIcon,
  Comment as CommentIcon,
  AttachFile as AttachFileIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { 
  PageContainer, 
  ContentContainer, 
  SectionHeader, 
  ModernButton, 
  GlassCard,
  GlassPaper,
  StatusChip,
  IconBox,
  AnimatedBox,
} from '../../styles/components/ModernComponents';

interface HelpSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  features: HelpFeature[];
  tips: string[];
}

interface HelpFeature {
  title: string;
  description: string;
  icon: React.ReactNode;
  steps?: string[];
  examples?: string[];
}

const helpSections: HelpSection[] = [
  {
    id: 'task-management',
    title: 'Task Management',
    icon: <AssignmentIcon />,
    description: 'Create, organize, and track tasks for your release topics with comprehensive management capabilities.',
    features: [
      {
        title: 'Task Creation',
        description: 'Create new tasks with detailed information including title, description, priority, and assignments.',
        icon: <AddIcon />,
        steps: [
          'Click "Create Task" button in the Task Management section',
          'Fill in the required fields: Title, Description, Priority, Release Topic',
          'Set optional fields: Assignee, Estimated Hours, Due Date, Tags',
          'Click "Create Task" to save'
        ],
        examples: [
          'Title: "Implement User Authentication"',
          'Description: "Add OAuth2 authentication with Google and GitHub providers"',
          'Priority: HIGH',
          'Estimated Hours: 8'
        ]
      },
      {
        title: 'Task Listing & Search',
        description: 'View all tasks in a comprehensive table with advanced search and filtering capabilities.',
        icon: <SearchIcon />,
        steps: [
          'Use the search bar to find tasks by title or description',
          'Apply status filters (TODO, IN_PROGRESS, COMPLETED, etc.)',
          'Filter by priority (LOW, MEDIUM, HIGH, URGENT)',
          'Filter by release topic or assignee',
          'Clear filters to reset the view'
        ],
        examples: [
          'Search: "authentication" to find all auth-related tasks',
          'Status Filter: "IN_PROGRESS" to see active tasks',
          'Priority Filter: "HIGH, URGENT" for critical tasks'
        ]
      },
      {
        title: 'Task Details & Editing',
        description: 'View comprehensive task details and edit task information with real-time updates.',
        icon: <EditIcon />,
        steps: [
          'Click the "View Details" icon to open task details',
          'Review all task information in the detailed view',
          'Click "Edit" to modify task details',
          'Update fields as needed and click "Save Changes"',
          'Use "Cancel" to discard changes'
        ],
        examples: [
          'Update task status from "TODO" to "IN_PROGRESS"',
          'Change priority from "MEDIUM" to "HIGH"',
          'Add actual hours worked on the task',
          'Update due date if timeline changes'
        ]
      },
      {
        title: 'Task Status Management',
        description: 'Track task progress through different statuses and manage workflow transitions.',
        icon: <CheckCircleIcon />,
        steps: [
          'Click "Change Status" button in task details',
          'Select new status from the dropdown',
          'Available statuses: TODO, IN_PROGRESS, IN_REVIEW, BLOCKED, COMPLETED, CANCELLED',
          'Click "Update Status" to confirm'
        ],
        examples: [
          'TODO → IN_PROGRESS: When work begins',
          'IN_PROGRESS → IN_REVIEW: When ready for review',
          'IN_REVIEW → COMPLETED: When approved',
          'IN_PROGRESS → BLOCKED: When blocked by dependencies'
        ]
      },
      {
        title: 'Task Deletion',
        description: 'Remove tasks that are no longer needed with confirmation to prevent accidental deletion.',
        icon: <DeleteIcon />,
        steps: [
          'Click the "Delete" icon in the task list or details',
          'Review the confirmation dialog',
          'Confirm deletion by clicking "Delete"',
          'Task will be permanently removed from the system'
        ],
        examples: [
          'Delete duplicate tasks',
          'Remove cancelled or obsolete tasks',
          'Clean up test tasks after completion'
        ]
      }
    ],
    tips: [
      'Use descriptive task titles that clearly indicate the work required',
      'Set realistic estimated hours to help with project planning',
      'Regularly update task status to keep the team informed of progress',
      'Use tags to categorize tasks for better organization',
      'Add detailed descriptions to help assignees understand requirements'
    ]
  },
  {
    id: 'task-dashboard',
    title: 'Task Dashboard',
    icon: <DashboardIcon />,
    description: 'Get an overview of task progress, statistics, and key metrics for better project management.',
    features: [
      {
        title: 'Task Statistics',
        description: 'View comprehensive statistics about task distribution and progress.',
        icon: <InfoIcon />,
        steps: [
          'Access the Task Dashboard from the main navigation',
          'Review total tasks count and completion status',
          'Check tasks by priority distribution',
          'Monitor overdue tasks and time tracking'
        ],
        examples: [
          'Total Tasks: 25, Completed: 18, In Progress: 5',
          'Priority Distribution: HIGH (8), MEDIUM (12), LOW (5)',
          'Overdue Tasks: 2 tasks past due date'
        ]
      },
      {
        title: 'Progress Tracking',
        description: 'Monitor project progress through visual indicators and time tracking.',
        icon: <PlayArrowIcon />,
        steps: [
          'View progress bars for time tracking',
          'Monitor actual vs estimated hours',
          'Check completion percentages',
          'Review due date status and overdue indicators'
        ],
        examples: [
          'Progress: 6h actual / 8h estimated (75% complete)',
          'Due Date: Aug 30, 2025 (On track)',
          'Status: IN_PROGRESS with 2 days remaining'
        ]
      }
    ],
    tips: [
      'Regularly review dashboard metrics to identify bottlenecks',
      'Use progress tracking to adjust project timelines',
      'Monitor overdue tasks to prevent project delays',
      'Check time tracking accuracy for better future estimates'
    ]
  },
  {
    id: 'task-details',
    title: 'Task Details & Collaboration',
    icon: <ViewIcon />,
    description: 'Comprehensive task view with collaboration features including comments and attachments.',
    features: [
      {
        title: 'Detailed Task View',
        description: 'View all task information in an organized, tabbed interface.',
        icon: <InfoIcon />,
        steps: [
          'Navigate to task details using the "View Details" icon',
          'Review information in the Details tab',
          'Check task metadata in the sidebar',
          'View progress indicators and time tracking'
        ],
        examples: [
          'Task ID: GLA-00001',
          'Created: Aug 30, 2025',
          'Reporter: John Doe',
          'Release Topic: User Authentication Module'
        ]
      },
      {
        title: 'Comments & Collaboration',
        description: 'Add comments to tasks for team communication and progress updates.',
        icon: <CommentIcon />,
        steps: [
          'Navigate to the Comments tab in task details',
          'Type your comment in the text area',
          'Click "Add Comment" to post',
          'View all comments with timestamps and authors'
        ],
        examples: [
          'Comment: "Started implementing OAuth2 flow"',
          'Comment: "Need clarification on password requirements"',
          'Comment: "Code review completed, ready for testing"'
        ]
      },
      {
        title: 'File Attachments',
        description: 'Attach relevant files to tasks for documentation and reference.',
        icon: <AttachFileIcon />,
        steps: [
          'Navigate to the Attachments tab in task details',
          'Click "Upload File" to add attachments',
          'Select files from your device',
          'View and download attached files',
          'Delete attachments if no longer needed'
        ],
        examples: [
          'Attach design mockups for UI tasks',
          'Upload technical specifications',
          'Add test results or bug reports',
          'Include relevant documentation'
        ]
      }
    ],
    tips: [
      'Use comments to provide context and updates to team members',
      'Attach relevant files to keep all information in one place',
      'Regularly review and clean up outdated attachments',
      'Use comments to ask questions and get clarifications'
    ]
  },
  {
    id: 'filters-and-search',
    title: 'Advanced Search & Filtering',
    icon: <FilterIcon />,
    description: 'Powerful search and filtering capabilities to quickly find and organize tasks.',
    features: [
      {
        title: 'Text Search',
        description: 'Search tasks by title, description, or any text content.',
        icon: <SearchIcon />,
        steps: [
          'Enter search terms in the search bar',
          'Press Enter or click the search icon',
          'Results will show tasks matching your search',
          'Clear search to show all tasks'
        ],
        examples: [
          'Search "authentication" to find all auth-related tasks',
          'Search "bug" to find bug fix tasks',
          'Search "UI" to find user interface tasks'
        ]
      },
      {
        title: 'Status Filtering',
        description: 'Filter tasks by their current status to focus on specific workflow stages.',
        icon: <CheckCircleIcon />,
        steps: [
          'Select one or more statuses from the Status dropdown',
          'Choose from: TODO, IN_PROGRESS, IN_REVIEW, BLOCKED, COMPLETED, CANCELLED',
          'View only tasks with selected statuses',
          'Clear filter to show all statuses'
        ],
        examples: [
          'Filter by "IN_PROGRESS" to see active tasks',
          'Filter by "COMPLETED" to review finished work',
          'Filter by "BLOCKED" to identify issues'
        ]
      },
      {
        title: 'Priority Filtering',
        description: 'Filter tasks by priority level to focus on high-impact work.',
        icon: <FlagIcon />,
        steps: [
          'Select one or more priorities from the Priority dropdown',
          'Choose from: LOW, MEDIUM, HIGH, URGENT',
          'View only tasks with selected priorities',
          'Clear filter to show all priorities'
        ],
        examples: [
          'Filter by "HIGH, URGENT" to focus on critical tasks',
          'Filter by "LOW" to see low-priority items',
          'Filter by "MEDIUM" for balanced workload'
        ]
      },
      {
        title: 'Release Topic Filtering',
        description: 'Filter tasks by release topic to focus on specific project areas.',
        icon: <TopicIcon />,
        steps: [
          'Select a release topic from the dropdown',
          'View only tasks associated with that topic',
          'Choose "All Release Topics" to see everything',
          'Clear filter to show all topics'
        ],
        examples: [
          'Filter by "User Authentication" topic',
          'Filter by "Payment Integration" topic',
          'Filter by "Mobile App" topic'
        ]
      }
    ],
    tips: [
      'Combine multiple filters for precise task targeting',
      'Use search and filters together for best results',
      'Save common filter combinations for quick access',
      'Clear filters regularly to avoid missing important tasks'
    ]
  },
  {
    id: 'task-organization',
    title: 'Task Organization & Workflow',
    icon: <AssignmentIcon />,
    description: 'Organize tasks effectively with proper workflow management and status tracking.',
    features: [
      {
        title: 'Task Status Workflow',
        description: 'Follow a structured workflow to manage task progression from creation to completion.',
        icon: <PlayArrowIcon />,
        steps: [
          'Create task with TODO status',
          'Move to IN_PROGRESS when work begins',
          'Transition to IN_REVIEW when ready for review',
          'Mark as COMPLETED when approved',
          'Use BLOCKED status for dependencies or issues'
        ],
        examples: [
          'TODO → IN_PROGRESS: Development starts',
          'IN_PROGRESS → IN_REVIEW: Code ready for review',
          'IN_REVIEW → COMPLETED: Review approved',
          'IN_PROGRESS → BLOCKED: Waiting for API changes'
        ]
      },
      {
        title: 'Priority Management',
        description: 'Set and manage task priorities to ensure important work gets attention.',
        icon: <FlagIcon />,
        steps: [
          'Set priority when creating or editing tasks',
          'Use URGENT for critical, time-sensitive items',
          'Use HIGH for important but not urgent tasks',
          'Use MEDIUM for standard priority work',
          'Use LOW for nice-to-have features'
        ],
        examples: [
          'URGENT: Security vulnerabilities, production bugs',
          'HIGH: Core features, user-facing improvements',
          'MEDIUM: Enhancements, optimizations',
          'LOW: Documentation, minor UI tweaks'
        ]
      },
      {
        title: 'Time Tracking',
        description: 'Track estimated vs actual time to improve project planning and resource allocation.',
        icon: <ScheduleIcon />,
        steps: [
          'Set estimated hours when creating tasks',
          'Update actual hours as work progresses',
          'Monitor progress through the dashboard',
          'Use data to improve future estimates'
        ],
        examples: [
          'Estimated: 8h, Actual: 6h (Under estimate)',
          'Estimated: 4h, Actual: 8h (Over estimate)',
          'Progress: 75% complete based on time tracking'
        ]
      }
    ],
    tips: [
      'Regularly update task status to keep workflow moving',
      'Use priority levels consistently across the team',
      'Track time accurately to improve future estimates',
      'Review workflow bottlenecks and optimize processes'
    ]
  }
];

export default function HelpDocumentation() {
  const [expandedSection, setExpandedSection] = useState<string | false>(false);
  const [selectedFeature, setSelectedFeature] = useState<HelpFeature | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleSectionChange = (sectionId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? sectionId : false);
  };

  const handleFeatureClick = (feature: HelpFeature) => {
    setSelectedFeature(feature);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedFeature(null);
  };

  return (
    <PageContainer>
      <ContentContainer>
        <AnimatedBox>
          {/* Header Section */}
          <SectionHeader>
            <Box display="flex" alignItems="center" gap={3}>
              <IconBox>
                <HelpIcon />
              </IconBox>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                  Task Management Help
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Comprehensive guide to all features and capabilities
                </Typography>
              </Box>
            </Box>
          </SectionHeader>

          {/* Quick Start Guide */}
          <GlassCard sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                🚀 Quick Start Guide
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      1. Create Your First Task
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Click "Create Task" and fill in the basic information including title, description, and priority.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      2. Organize with Filters
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Use the search and filter options to find specific tasks or view tasks by status and priority.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      3. Track Progress
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Update task status as work progresses and add comments for team collaboration.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      4. Monitor Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Check the dashboard for project overview, statistics, and progress tracking.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </GlassCard>

          {/* Feature Sections */}
          <Box>
            {helpSections.map((section) => (
              <Accordion
                key={section.id}
                expanded={expandedSection === section.id}
                onChange={handleSectionChange(section.id)}
                sx={{
                  mb: 2,
                  '&:before': { display: 'none' },
                  '& .MuiAccordionSummary-root': {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                    }
                  },
                  '& .MuiAccordionDetails-root': {
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '0 0 8px 8px',
                  }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box sx={{ color: 'white' }}>
                      {section.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                        {section.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {section.description}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    {/* Features */}
                    <Grid item xs={12} lg={8}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                        Features
                      </Typography>
                      <Grid container spacing={2}>
                        {section.features.map((feature, index) => (
                          <Grid item xs={12} md={6} key={index}>
                            <GlassCard 
                              sx={{ 
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                                }
                              }}
                              onClick={() => handleFeatureClick(feature)}
                            >
                              <CardContent>
                                <Box display="flex" alignItems="center" gap={2} mb={1}>
                                  <Box sx={{ color: 'primary.main' }}>
                                    {feature.icon}
                                  </Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {feature.title}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {feature.description}
                                </Typography>
                                <Box mt={1}>
                                  <Chip 
                                    label="Click for details" 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                  />
                                </Box>
                              </CardContent>
                            </GlassCard>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>

                    {/* Tips */}
                    <Grid item xs={12} lg={4}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                        💡 Pro Tips
                      </Typography>
                      <List>
                        {section.tips.map((tip, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <Box 
                                sx={{ 
                                  width: 8, 
                                  height: 8, 
                                  borderRadius: '50%', 
                                  background: 'primary.main' 
                                }} 
                              />
                            </ListItemIcon>
                            <ListItemText 
                              primary={tip}
                              primaryTypographyProps={{ 
                                variant: 'body2',
                                sx: { lineHeight: 1.5 }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* Keyboard Shortcuts */}
          <GlassCard sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                ⌨️ Keyboard Shortcuts
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Navigation
                  </Typography>
                  <List dense>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText 
                        primary="Ctrl/Cmd + K"
                        secondary="Quick search tasks"
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText 
                        primary="Ctrl/Cmd + N"
                        secondary="Create new task"
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText 
                        primary="Escape"
                        secondary="Close dialogs and modals"
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Actions
                  </Typography>
                  <List dense>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText 
                        primary="Ctrl/Cmd + S"
                        secondary="Save changes (when editing)"
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText 
                        primary="Ctrl/Cmd + Enter"
                        secondary="Submit forms"
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText 
                        primary="Delete"
                        secondary="Delete selected task"
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </GlassCard>
        </AnimatedBox>

        {/* Feature Detail Dialog */}
        <Dialog 
          open={dialogOpen} 
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={2}>
                <Box sx={{ color: 'primary.main' }}>
                  {selectedFeature?.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {selectedFeature?.title}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseDialog}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {selectedFeature?.description}
            </Typography>

            {selectedFeature?.steps && (
              <Box mb={3}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  How to Use
                </Typography>
                <List>
                  {selectedFeature.steps.map((step, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Box 
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            borderRadius: '50%', 
                            background: 'primary.main',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.875rem',
                            fontWeight: 600
                          }}
                        >
                          {index + 1}
                        </Box>
                      </ListItemIcon>
                      <ListItemText primary={step} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {selectedFeature?.examples && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Examples
                </Typography>
                <List>
                  {selectedFeature.examples.map((example, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Box 
                          sx={{ 
                            width: 6, 
                            height: 6, 
                            borderRadius: '50%', 
                            background: 'text.secondary' 
                          }} 
                        />
                      </ListItemIcon>
                      <ListItemText 
                        primary={example}
                        primaryTypographyProps={{ 
                          variant: 'body2',
                          sx: { fontStyle: 'italic' }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={handleCloseDialog}>
              Close
            </ModernButton>
          </DialogActions>
        </Dialog>
      </ContentContainer>
    </PageContainer>
  );
}
